import React, { useEffect, useState } from "react";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Download, Share, X } from "lucide-react";

const DISMISSED = "stride_install_dismissed";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

let deferred = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });
}

export function useInstall() {
  const [ready, setReady] = useState(!!deferred);
  useEffect(() => {
    const on = () => setReady(true);
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);
  const install = async () => {
    if (!deferred) return false;
    deferred.prompt();
    const res = await deferred.userChoice;
    deferred = null;
    setReady(false);
    return res?.outcome === "accepted";
  };
  return { canInstall: ready, install, installed: isStandalone(), ios: isIOS() };
}

export function InstallPrompt() {
  const { t } = useLang();
  const { canInstall, install, installed, ios } = useInstall();
  const [hidden, setHidden] = useState(localStorage.getItem(DISMISSED) === "1");

  const dismiss = () => { localStorage.setItem(DISMISSED, "1"); setHidden(true); };

  if (hidden || installed || (!canInstall && !ios)) return null;

  return (
    <div data-testid="install-prompt"
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:w-[380px] z-40 rounded-lg border border-slate-200 bg-white shadow-lg p-4">
      <button onClick={dismiss} aria-label={t("Fermer")} data-testid="install-dismiss"
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      <p className="text-sm font-semibold text-slate-900 pr-6">{t("Installer Stride sur mon téléphone")}</p>
      <p className="mt-1 text-xs text-slate-500">
        {t("L'application s'ouvre en plein écran et la saisie quotidienne reste disponible hors réseau.")}
      </p>
      {ios && !canInstall ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-600" data-testid="install-ios-hint">
          <Share className="h-3.5 w-3.5" /> {t("Sur iPhone : Partager, puis « Sur l'écran d'accueil ».")}
        </p>
      ) : (
        <Button onClick={async () => { const ok = await install(); if (ok) dismiss(); }}
          className="mt-3 h-10 w-full rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="install-btn">
          <Download className="h-4 w-4 mr-1.5" />{t("Installer")}
        </Button>
      )}
    </div>
  );
}

export default InstallPrompt;
